import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import swal from 'sweetalert2';
import router from '@/router';
import helper from '@/services/Helper';
import to from 'await-to-js';
import FileDownload from 'js-file-download';

const config: AxiosRequestConfig = {
    baseURL: '/',
    headers: {
        'Content-Type': 'application/json',
    },
};

interface ValidationErrorObject {
    [key: string]: string[];
}

export class ApiResult<T> {
    Error: AxiosError | null;
    ErrorMessage: string;
    ErrorCode: number | null = null;
    Data: T | null;
    hasValidationError: boolean;

    constructor(err: Error | null, res: AxiosResponse<T> | undefined) {
        this.Error = err as AxiosError;
        if (typeof this.Error?.response?.data === 'object' && this.Error?.response?.data !== null) {
            const data = this.Error.response.data as any;

            this.ErrorMessage = data?.detail ?? data?.title ?? data?.message ?? this.Error.message;
            this.ErrorCode = data?.errorCode || null;
        } else if (this.Error?.response?.data) {
            this.ErrorMessage = this.Error?.response?.data as any;
        } else {
            this.ErrorMessage = this.Error?.message;
        }
        this.Data = res?.data ?? null;
        this.hasValidationError = (this.Error?.response?.data as any)?.errors ? true : false;
    }

    validationErrorsList() {
        if (this.Error && this.Error.response && this.Error.response.data) {
            const errors = (this.Error.response.data as any).errors as ValidationErrorObject;
            if (!errors || Array.isArray(errors)) return null;
            return Object.keys(errors).map((key) => ({ key, msg: errors[key].join !== undefined ? errors[key]?.join(',') : '' }));
        }
    }
}

export class BaseApi {
    private api: AxiosInstance;

    public constructor() {
        this.api = axios.create(config);
        this.api.interceptors.request.use((param: AxiosRequestConfig) => {
            const token = helper.authToken();
            if (token) {
                param.headers = { ...param.headers, ...{ Authorization: 'Bearer ' + token } };
            }

            return param;
        });

        this.api.interceptors.response.use(
            (response) => {
                return response;
            },
            (error: AxiosError) => {
                if (error.response && error.response.status === 401) {
                    console.error('Unauthorized request to API', error);
                    return new Promise(function (resolve) {
                        swal.fire('Unauthorized', '', 'warning').then(() => {
                            router.push({ name: 'login' });
                            resolve({ data: { ErrorMessage: null } });
                        });
                    });
                }
                if (error.response && error.response.status === 403) {
                    console.error('No permission request to API', error);
                    return { data: { ErrorMessage: 'general.permission.accessdenied' } };
                }
                return Promise.reject(error);
            }
        );
    }

    public async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResult<T>> {
        const [axiosError, axiosResponse] = await to(this.api.get(url, config));
        return new ApiResult(axiosError, axiosResponse);
    }

    public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResult<T>> {
        const [axiosError, axiosResponse] = await to(this.api.delete(url, config));
        return new ApiResult(axiosError, axiosResponse);
    }

    public async post<T>(url: string, data?: string, config?: AxiosRequestConfig): Promise<ApiResult<T>> {
        const [axiosError, axiosResponse] = await to(this.api.post(url, data, config));
        return new ApiResult(axiosError, axiosResponse);
    }
    public async patch<T>(url: string, data?: string, config?: AxiosRequestConfig): Promise<ApiResult<T>> {
        const [axiosError, axiosResponse] = await to(this.api.patch(url, data, config));
        return new ApiResult(axiosError, axiosResponse);
    }

    public async postFormData<T>(url: string, data: FormData, config?: AxiosRequestConfig): Promise<ApiResult<T>> {
        const [axiosError, axiosResponse] = await to(this.api.post(url, data, config));
        return new ApiResult(axiosError, axiosResponse);
    }

    public async put<T>(url: string, data?: string, config?: AxiosRequestConfig): Promise<ApiResult<T>> {
        const [axiosError, axiosResponse] = await to(this.api.put(url, data, config));
        return new ApiResult(axiosError, axiosResponse);
    }

    public async getFile(url: string, config?: AxiosRequestConfig): Promise<boolean> {
        const [axiosError, axiosResponse] = await to(this.api.get(url, { responseType: 'blob', ...config }));
        if (axiosError || !axiosResponse) return false;
        let fileName = '';
        const disposition = axiosResponse.headers['content-disposition'];
        if (disposition && disposition.indexOf('attachment') !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
                fileName = matches[1].replace(/['"]/g, '');
            }
        }
        FileDownload(axiosResponse.data, fileName || 'report.csv');
        return true;
    }
}
